import { useAuth } from "context/AuthContext"
import React, { useState } from "react"
import { useMediaQuery } from "react-responsive"
import { toast } from "react-toastify"
import { getOrCreateConversation } from "services/firestore/getOrCreateConversation"
import styled from "styled-components"
import { ButtonPrimary } from "ui/base/Button/ButtonStyled"
import { ClassInfoCard } from "ui/base/class/components/ClassInfoCard"
import { LoadingSpinner } from "ui/base/loading/LoadingSpinner"
import { Modal } from "ui/base/modal/Modal"
import { TextArea } from "ui/base/textArea/TextArea"
import { AlertCircle } from "ui/icons/AlertCircle"
import { defaultThemeColors } from "ui/theme"
import { updateTypingStatus } from "../../../../services/database/updateTypingStatus"
import { sendMessage } from "../../../../services/firestore/sendMessage"
import { track } from "analytics"
import { GUEST_MESSAGING_ENABLED } from "conts/features"
import { GuestMessageForm } from "./GuestMessageForm"
import { GuestMessageSent } from "./GuestMessageSent"
import { getAvatarUrl } from "utils"

// A message typed by a guest whose email already has an account is kept
// here while they log in, then pre-filled in the regular form.
const draftKey = (teacherUserId) => `teache_message_draft_${teacherUserId}`

export const SendMessageModal = ({
  classDetail,
  onSignupModal,
  onLoginModal,
}) => {
  const { userData, userRole, token } = useAuth()
  const [show, setShow] = useState(false)
  const [text, setText] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const [isMsgSent, setIsMsgSent] = useState(false)
  const [sentInstructorName, setSentInstructorName] = useState("")
  const [sentEmail, setSentEmail] = useState("")
  const [formStarted, setFormStarted] = useState(false)
  // Visitors who aren't logged in get the guest form (name, email, message)
  // only when the feature is enabled; otherwise the original sign-up flow.
  const isGuestMode = !token && GUEST_MESSAGING_ENABLED
  const teacherInfo = classDetail?.teacher

  const creatorId = userData?.id?.toString()
  const creatorRole = userRole
  const creatorName = `${userData?.first_name} ${
    userData?.last_name.split("")[0]
  }`
  const creatorProfilePicture =
    userRole === 1 ? userData?.profile_pic : userData?.teacher?.profile_pic

  const participantId = classDetail?.id?.toString()
  const receiverUserName = `${classDetail?.first_name} ${
    classDetail?.last_name?.split("")[0]
  }`
  const participantProfilePicture = teacherInfo?.profile_pic
  const participantRole = userRole === 1 ? 2 : 1

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" })

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!text.trim()) return

    track("message_submitted", {
      teacher_user_id: participantId,
      guest: false,
      length: text.trim().length,
    })
    setIsLoading(true)

    try {
      if (!participantId || !creatorId) {
        throw new Error("Sender or receiver ID not found")
      }

      // Create (or find) the conversation only when a message is actually
      // sent, so opening an instructor page never creates empty conversations.
      const conversationId = await getOrCreateConversation(
        creatorId,
        creatorName,
        creatorProfilePicture,
        creatorRole,
        participantId,
        receiverUserName,
        participantProfilePicture,
        participantRole
      )
      if (!conversationId) {
        throw new Error("Conversation could not be created")
      }

      updateTypingStatus(conversationId, creatorId, false)

      await sendMessage(
        text,
        conversationId,
        creatorId,
        participantId,
        receiverUserName
      )

      setText("")

      setIsMsgSent(true)
    } catch (error) {
      console.error("Error sending message:", error)
      track("message_send_failed", { teacher_user_id: participantId, guest: false })
      toast.error("An error occured while sending a message.")
    } finally {
      setIsLoading(false)
    }
  }

  const toggleInstructionsModal = () => {
    track("send_message_clicked", {
      teacher_user_id: participantId,
      logged_in: Boolean(token),
    })
    if (!token && !isGuestMode) {
      onSignupModal(true)
      return
    }
    if (token) {
      try {
        const draft = window.sessionStorage.getItem(draftKey(participantId))
        if (draft) {
          setText(draft)
          window.sessionStorage.removeItem(draftKey(participantId))
        }
      } catch {
        // ignore
      }
    }
    setFormStarted(false)
    setIsMsgSent(false)
    setShow((prev) => !prev)
  }

  const handleTextChange = (value) => {
    if (!formStarted) {
      setFormStarted(true)
      track("message_form_started", { teacher_user_id: participantId, guest: false })
    }
    setText(value)
  }

  const handleGuestSent = ({ instructorName, email }) => {
    setSentInstructorName(
      instructorName || classDetail?.first_name || "the instructor"
    )
    setSentEmail(email || "")
    setIsMsgSent(true)
  }

  const handleGuestLoginRequired = (draft) => {
    try {
      window.sessionStorage.setItem(draftKey(participantId), draft || "")
    } catch {
      // ignore
    }
    setShow(false)
    onLoginModal?.(true)
  }

  return (
    <>
      <SendMessageBtn onClick={toggleInstructionsModal}>
        Send a message
      </SendMessageBtn>
      {isGuestMode ? (
        <Modal
          show={show}
          handleClose={() => setShow(false)}
          maxWidth={isMsgSent ? 440 : 560}
          minHeight={0}>
          <GuestBody>
            {show &&
              (isMsgSent ? (
                <GuestMessageSent
                  instructorName={sentInstructorName}
                  email={sentEmail}
                  onDone={() => setShow(false)}
                />
              ) : (
                <GuestMessageForm
                  teacherUserId={participantId}
                  instructorFirstName={classDetail?.first_name}
                  instructorPhoto={getAvatarUrl(teacherInfo?.profile_pic)}
                  subject={teacherInfo?.subject_sub_category}
                  onSent={handleGuestSent}
                  onLoginRequired={handleGuestLoginRequired}
                />
              ))}
          </GuestBody>
        </Modal>
      ) : (
      <Modal
        show={show}
        handleClose={() => setShow(false)}
        maxWidth={isMsgSent ? 500 : 900}>
        <ModalBody>
          {!isMsgSent ? (
            <>
              <LeftPart isMobile={isMobile}>
                <ClassInfoCard
                  classInfo={{
                    first_name: classDetail?.first_name,
                    profile_pic: teacherInfo?.profile_pic,
                    class: teacherInfo?.subject_sub_category,
                    rating: teacherInfo?.rate,
                    class_type: teacherInfo?.type_of_class,
                    review: teacherInfo?.review,
                    online_type: teacherInfo?.online_type,
                  }}
                />
              </LeftPart>
              <RightPart isMobile={isMobile}>
                <div>
                  <TextArea
                    label={
                      !isMobile ? "Write your message to the instructor" : ""
                    }
                    placeholder='Type message...'
                    style={{ height: 338 }}
                    value={text}
                    setValue={handleTextChange}
                  />
                </div>
                <ButtonContainer>
                  <ButtonPrimary
                    style={{
                      width: "100%",
                    }}
                    disabled={!text || isLoading}
                    onClick={handleSendMessage}>
                    {isLoading ? (
                      <>
                        <LoadingSpinner variant='white' />
                        <span
                          style={{
                            color: "#d9d9d9",
                          }}>
                          Loading...
                        </span>
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </ButtonPrimary>
                </ButtonContainer>
              </RightPart>
            </>
          ) : (
            <ThanksContent>
              <AlertWrap>
                <AlertCircle />
              </AlertWrap>
              <Title>Message Sent!</Title>
              <Message>
                When the instructor replies, you will see it on the messages
                page.
              </Message>
              <FullButtonPrimary onClick={() => setShow(false)}>
                Done
              </FullButtonPrimary>
            </ThanksContent>
          )}
        </ModalBody>
      </Modal>
      )}
    </>
  )
}

const GuestBody = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 4px 4px 8px;

  @media (min-width: 768px) {
    padding: 0 16px 4px;
  }
`

const SendMessageBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
  background: transparent;
  border: 2px solid #1d1d1f;
  border-radius: 100px;
  padding: 12px 24px;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    border-color: #1d1d1f;
    background: rgba(29, 29, 31, 0.04);
  }
`

const ModalBody = styled.div`
  display: flex;
  padding-block: 1rem;
  padding-inline: 1.5rem;
  gap: 2rem;
`

const LeftPart = styled.div`
  width: 40%;
  display: ${({ isMobile }) => (isMobile ? "none" : "block")};

  & > img {
    object-fit: cover;
    max-width: 280px;
    width: 100%;
    aspect-ratio: 28 / 17;
    border-radius: 5px;
    border-width: 1px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
  }
`

const RightPart = styled.div`
  width: ${({ isMobile }) => (isMobile ? "100%" : "60%")};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ThanksContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  /* margin-bottom: 3rem; */
`

const AlertWrap = styled.div`
  margin-bottom: 12px;
  padding: 20px;
  width: 45px;
  height: 45px;
  box-sizing: content-box;
  border-radius: 50%;
  background-color: #edf0ff;
`

const Title = styled.h2`
  font-weight: 600;
  font-size: 26px;
  letter-spacing: 0%;
  text-align: center;
`

const Message = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 155%;
  letter-spacing: 0%;
  text-align: center;
`

const FullButtonPrimary = styled(ButtonPrimary)`
  width: calc(100% - 60px);
  margin-top: 20px;
`

const ButtonContainer = styled.div`
  display: flex;
`
